/**
 * GT-GFI-AI-PROVIDER-COMPARISON-SMOKE-WORKBENCH-01 (DL-112) — pure,
 * side-effect-free model for the Platform-Owner Provider Comparison &
 * Smoke Workbench.
 *
 * This module holds the **closed control catalogs** (supported GFI
 * questions, available AI report types, run modes), the result-row shape
 * (mirrors the backend `ProviderComparisonRow` plus display-only fields),
 * the highlight classifier, the **clearly-labelled dry-run preview** row
 * builder, and the smoke checklist. It is deliberately UI- and
 * backend-free so it unit-tests without a render or a network.
 *
 * Safety posture (re-proven here):
 *   - Every catalog is a CLOSED allowlist. The workbench cannot express a
 *     free-text model id, a free-text prompt, a provider-selected data
 *     source, or any SQL — the controls only ever pick from these lists +
 *     the fetched provider registry.
 *   - The preview builder makes NO provider call. Every preview row has
 *     `providerCalled = false`, `preview = true`, and cost `unverified`.
 *     It renders representative EXAMPLE outcomes so the table + highlight
 *     states are demonstrable before live activation — never a real result.
 *   - No secret / env value / raw prompt / raw response is modelled here.
 */

/** Test type the operator is comparing. Closed. */
export const WORKBENCH_TEST_TYPES = ["gfi_question", "ai_report"] as const;
export type WorkbenchTestType = (typeof WORKBENCH_TEST_TYPES)[number];

/** AI capability id a test type maps onto (mirrors `@genius-tracker/ai`). */
export type WorkbenchCapability = "gfi_ai_questions" | "ai_reports";

export function capabilityForTestType(testType: WorkbenchTestType): WorkbenchCapability {
  return testType === "ai_report" ? "ai_reports" : "gfi_ai_questions";
}

/** Run modes: one selection, or the full compare matrix. Closed. */
export const WORKBENCH_RUN_MODES = ["single", "compare"] as const;
export type WorkbenchRunMode = (typeof WORKBENCH_RUN_MODES)[number];

/**
 * Closed list of supported GFI questions offered as chips. Each maps to a
 * real closed-registry intent (see
 * `services/api/src/modules/fleet-intelligence/intents/*`); the operator
 * never types a free-text question. `i18nKey` localizes the chip label.
 */
export interface WorkbenchQuestion {
  readonly id: string;
  readonly i18nKey: string;
  /** The closed-registry intent this question resolves to (for the live route). */
  readonly intent: string;
}

export const WORKBENCH_GFI_QUESTIONS: readonly WorkbenchQuestion[] = [
  { id: "offline", i18nKey: "admin.ai_workbench.question.offline", intent: "offline_devices" },
  {
    id: "not_moved",
    i18nKey: "admin.ai_workbench.question.not_moved",
    intent: "vehicles_not_moved",
  },
  {
    id: "maintenance_overdue",
    i18nKey: "admin.ai_workbench.question.maintenance_overdue",
    intent: "maintenance_overdue",
  },
  {
    id: "top_distance",
    i18nKey: "admin.ai_workbench.question.top_distance",
    intent: "top_distance",
  },
  { id: "fuel_events", i18nKey: "admin.ai_workbench.question.fuel_events", intent: "fuel_events" },
];

/**
 * Closed list of the AVAILABLE AI report types (mirrors the `available`
 * entries of `AI_REPORT_TYPES`; the `deferred` utilization type is
 * intentionally excluded — never offered, never faked).
 */
export interface WorkbenchReportType {
  readonly id: string;
  readonly i18nKey: string;
}

export const WORKBENCH_REPORT_TYPES: readonly WorkbenchReportType[] = [
  { id: "fleet_overview", i18nKey: "admin.ai_workbench.report.fleet_overview" },
  { id: "maintenance_risk_snapshot", i18nKey: "admin.ai_workbench.report.maintenance_risk" },
  { id: "alert_exception_snapshot", i18nKey: "admin.ai_workbench.report.alert_exception" },
];

/**
 * Closed status union — mirrors the backend `AiInvocationStatus`
 * (`services/api/src/modules/ai-runtime/gateway.dto.ts`) so the live
 * route's rows map straight onto a workbench row. Kept as a local union so
 * the admin app never imports a backend type.
 */
export type WorkbenchStatus =
  | "ok"
  | "disabled"
  | "dry_run"
  | "provider_call_blocked"
  | "quota_exceeded"
  | "invalid_selection"
  | "provider_error"
  | "timeout";

/** Coarse highlight bucket for the result table (drives colour + label). */
export type WorkbenchHighlight = "valid" | "fallback" | "timeout" | "quota" | "invalid" | "inert";

/**
 * Classify a result row into a highlight bucket. Pure; covers both the
 * dry-run preview and (later) the live route's rows. The order matters: a
 * fallback row can carry a non-`ok` status, so the explicit terminal
 * states are checked first.
 */
export function highlightFor(row: {
  readonly status: WorkbenchStatus;
  readonly fallback: boolean;
}): WorkbenchHighlight {
  switch (row.status) {
    case "ok":
      return "valid";
    case "timeout":
      return "timeout";
    case "quota_exceeded":
      return "quota";
    case "invalid_selection":
      return "invalid";
    case "disabled":
    case "dry_run":
      return "inert";
    case "provider_call_blocked":
    case "provider_error":
      return "fallback";
    default:
      return row.fallback ? "fallback" : "inert";
  }
}

export const WORKBENCH_HIGHLIGHTS: readonly WorkbenchHighlight[] = [
  "valid",
  "fallback",
  "timeout",
  "quota",
  "invalid",
  "inert",
];

/** i18n key for a highlight bucket's label. */
export function highlightLabelKey(h: WorkbenchHighlight): string {
  return `admin.ai_workbench.highlight.${h}`;
}

/** i18n key for a status badge. */
export function statusLabelKey(s: WorkbenchStatus): string {
  return `admin.ai_workbench.status.${s}`;
}

/**
 * One result row. Mirrors the backend `ProviderComparisonRow` (provider,
 * model, experimental, status, providerCalled, fallback, answerStatus,
 * prompt/completion tokens) and adds display-only fields the table shows
 * (cost status, invocationId, feedback availability, and the `preview`
 * marker). Cost is a STATUS, never a fabricated number.
 */
export interface WorkbenchResultRow {
  readonly provider: string;
  readonly model: string;
  readonly experimental: boolean;
  readonly status: WorkbenchStatus;
  readonly providerCalled: boolean;
  readonly fallback: boolean;
  readonly answerStatus: string | null;
  readonly promptTokens: number | null;
  readonly completionTokens: number | null;
  /** "unverified" while `AI_PRICING_VERIFIED=false`; never "$0.00". */
  readonly costStatus: "unverified" | "verified";
  /** Opaque ledger id — only on a live run; null in preview. Never a secret. */
  readonly invocationId: string | null;
  /** Whether answer feedback can be attached (true only on a live `ok` row). */
  readonly feedbackAvailable: boolean;
  /** True = a clearly-labelled dry-run example, NOT a live provider call. */
  readonly preview: boolean;
}

/** A chosen (provider, model) combination to compare. */
export interface WorkbenchSelection {
  readonly provider: string;
  readonly model: string;
  readonly experimental: boolean;
}

/**
 * Representative archetype statuses cycled across the preview rows so all
 * highlight states are demonstrable in the table before live activation.
 * Order chosen so the FIRST selection always shows the happy path.
 */
const PREVIEW_ARCHETYPES: readonly WorkbenchStatus[] = [
  "ok",
  "provider_call_blocked",
  "timeout",
  "quota_exceeded",
  "invalid_selection",
  "dry_run",
];

/**
 * Build the **dry-run preview** rows for a set of selections. Every row is
 * a clearly-labelled EXAMPLE (`preview: true`, `providerCalled: false`,
 * cost `unverified`) — no provider is called, no result is fabricated as
 * "real". Archetype statuses are assigned by position purely to
 * demonstrate the table's highlight states; they are NOT a judgement about
 * any provider. The live route (deferred) returns rows of the same shape
 * with `preview: false` and real (still secret-free) values.
 */
export function buildPreviewRows(
  selections: readonly WorkbenchSelection[],
): readonly WorkbenchResultRow[] {
  return selections.map((sel, i) => {
    const status = PREVIEW_ARCHETYPES[i % PREVIEW_ARCHETYPES.length] ?? "dry_run";
    const isOk = status === "ok";
    const fallback =
      status === "provider_call_blocked" ||
      status === "provider_error" ||
      status === "timeout" ||
      status === "quota_exceeded";
    return {
      provider: sel.provider,
      model: sel.model,
      experimental: sel.experimental,
      status,
      providerCalled: false,
      fallback,
      answerStatus: isOk ? "ok" : null,
      // Token metadata is illustrative only in preview; null elsewhere so
      // the table shows an honest "—".
      promptTokens: isOk ? 128 : null,
      completionTokens: isOk ? 64 : null,
      costStatus: "unverified",
      invocationId: null,
      feedbackAvailable: false,
      preview: true,
    };
  });
}

/**
 * The smoke checklist. A structured, manual list the Owner walks during a
 * staging comparison. `i18nKey` localizes the item; the operator checks it
 * off client-side (no persistence). Items reference env vars only as
 * "SET (redacted)" — never a value.
 */
export interface SmokeChecklistItem {
  readonly id: string;
  readonly i18nKey: string;
}

export const WORKBENCH_SMOKE_CHECKLIST: readonly SmokeChecklistItem[] = [
  { id: "env_keys", i18nKey: "admin.ai_workbench.check.env_keys" },
  { id: "gfi_enabled", i18nKey: "admin.ai_workbench.check.gfi_enabled" },
  { id: "reports_enabled", i18nKey: "admin.ai_workbench.check.reports_enabled" },
  { id: "mode_enabled", i18nKey: "admin.ai_workbench.check.mode_enabled" },
  { id: "provider_model", i18nKey: "admin.ai_workbench.check.provider_model" },
  { id: "daily_limit", i18nKey: "admin.ai_workbench.check.daily_limit" },
  { id: "ask_question", i18nKey: "admin.ai_workbench.check.ask_question" },
  { id: "generate_report", i18nKey: "admin.ai_workbench.check.generate_report" },
  { id: "submit_feedback", i18nKey: "admin.ai_workbench.check.submit_feedback" },
  { id: "verify_usage", i18nKey: "admin.ai_workbench.check.verify_usage" },
  { id: "verify_safe_paths", i18nKey: "admin.ai_workbench.check.verify_safe_paths" },
  { id: "verify_no_raw", i18nKey: "admin.ai_workbench.check.verify_no_raw" },
];
